import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Waves, Bell, LifeBuoy, ChevronDown, KeyRound, Send } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TELEGRAM_SUPPORT = "https://t.me/WXSyncVods_bot";

export const Navbar = () => {
  const { lang, setLang, t } = useI18n();
  const [code, setCode] = useState("");
  const [announcements, setAnnouncements] = useState([]);
  const langs = [{ k: "BR", label: "Português" }, { k: "EN", label: "English" }, { k: "ES", label: "Español" }];

  useEffect(() => {
    axios.get(`${API}/announcements`).then((r) => setAnnouncements(r.data.announcements || [])).catch(() => {});
  }, []);

  const openPanel = () => {
    const clean = code.trim();
    if (clean) window.location.href = `/panel/${clean}`;
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-slate-200 bg-white/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-600 text-white">
            <Waves className="h-5 w-5" />
          </span>
          <span className="font-display text-xl font-extrabold tracking-tight text-green-700">
            WX SyncVods
          </span>
        </Link>

        <div className="flex items-center gap-1 sm:gap-3">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="navbar-avisos-btn"
                className="text-slate-600 hover:text-green-700">
                <Bell className="mr-1.5 h-4 w-4" /> {t("nav.avisos")}
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="avisos-modal">
              <DialogHeader>
                <DialogTitle className="font-display">{t("nav.avisos")}</DialogTitle>
                <DialogDescription>{t("nav.avisosDesc")}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm text-slate-600" data-testid="avisos-list">
                {announcements.map((a) => <p key={a.id}>• {a.text}</p>)}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="navbar-suporte-btn"
                className="text-slate-600 hover:text-green-700">
                <LifeBuoy className="mr-1.5 h-4 w-4" /> {t("nav.suporte")}
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="suporte-modal">
              <DialogHeader>
                <DialogTitle className="font-display">{t("nav.suporte")}</DialogTitle>
                <DialogDescription>{t("nav.suporteDesc")}</DialogDescription>
              </DialogHeader>
              <p className="text-sm text-slate-600">{t("nav.suporteBody")}</p>
              <a href={TELEGRAM_SUPPORT} target="_blank" rel="noopener noreferrer" data-testid="suporte-telegram-link"
                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sky-700">
                <Send className="h-4 w-4" /> {t("nav.telegramBtn")}
              </a>
            </DialogContent>
          </Dialog>

          <Dialog>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="navbar-panel-btn"
                className="bg-green-600 text-white hover:bg-green-700">
                <KeyRound className="mr-1.5 h-4 w-4" /> {t("nav.meuPainel")}
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="access-panel-modal">
              <DialogHeader>
                <DialogTitle className="font-display">{t("nav.acessar")}</DialogTitle>
                <DialogDescription>{t("nav.acessarDesc")}</DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-3">
                <Input data-testid="access-code-input" placeholder={t("nav.codePlaceholder")}
                  value={code} onChange={(e) => setCode(e.target.value)} className="font-mono-code text-xs" />
                <Button data-testid="access-code-submit" onClick={openPanel}
                  className="bg-green-600 text-white hover:bg-green-700">{t("nav.entrar")}</Button>
              </div>
            </DialogContent>
          </Dialog>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="lang-selector"
                className="border-slate-300 text-slate-700">
                {lang} <ChevronDown className="ml-1 h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {langs.map((l) => (
                <DropdownMenuItem key={l.k} data-testid={`lang-${l.k}`} onClick={() => setLang(l.k)}>
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
